import { useCallback, useEffect, useState } from "react";
import { useGraphToken } from "../auth/useGraphToken";
import { fetchChampions, type LoadedChampions } from "../api/champions";
import { DEFAULT_CONFIG, type ChampionsConfig } from "../data/championsDirectory";

export type ChampionsConfigState = {
  /** What's in SharePoint, or the bundled roster if it can't be reached. */
  config: ChampionsConfig;
  /** The loaded lists, needed to save changes; null until loaded. */
  loaded: LoadedChampions | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
};

/** The AI Champions page's content from the Champion lists in SharePoint. */
export function useChampionsConfig(): ChampionsConfigState {
  const getToken = useGraphToken();
  const [loaded, setLoaded] = useState<LoadedChampions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchChampions(await getToken());
      setLoaded(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { config: loaded?.config ?? DEFAULT_CONFIG, loaded, loading, error, reload };
}
